import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Helmet } from 'react-helmet';
import { ChevronRight, Clock, ArrowLeft, MessageCircle, User, Calendar } from 'lucide-react';
import { blogPosts } from '@/data/blogData';

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  const relatedPosts = useMemo(() => {
    if (!post) return [];
    return blogPosts.filter((p) => p.id !== post.id).slice(0, 3);
  }, [post]);

  if (!post) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex flex-col items-center justify-center pt-32">
          <h1 className="text-3xl font-bold text-foreground mb-4">Article Not Found</h1>
          <p className="text-muted-foreground mb-6">The article you&apos;re looking for doesn&apos;t exist.</p>
          <Button asChild>
            <Link to="/blog">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Link>
          </Button>
        </div>
      </Layout>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    image: post.image,
    author: { '@type': 'Organization', name: 'NutraFuel Supplements' },
    publisher: { '@type': 'Organization', name: 'NutraFuel Supplements' },
    datePublished: post.date,
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://nutrafuel.in/blog/${post.slug}` },
  };

  const faqJsonLd = post.faq && post.faq.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: post.faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  } : null;

  return (
    <>
      <Helmet>
        <title>{`${post.title} | NutraFuel Blog`}</title>
        <meta name="description" content={post.excerpt} />
        <meta name="keywords" content={post.tags.join(', ')} />
        <link rel="canonical" href={`https://nutrafuel.in/blog/${post.slug}`} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={post.image} />
        <meta property="og:url" content={`https://nutrafuel.in/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        {faqJsonLd && <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>}
      </Helmet>

      <Layout>
        {/* Breadcrumb */}
        <div className="bg-muted/30 pt-24 pb-3 border-b border-border">
          <div className="container mx-auto px-4">
            <nav className="flex items-center text-sm text-muted-foreground" data-testid="blog-breadcrumb">
              <Link to="/" className="hover:text-primary transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5 mx-2" />
              <Link to="/blog" className="hover:text-primary transition-colors">Blog</Link>
              <ChevronRight className="w-3.5 h-3.5 mx-2" />
              <span className="text-foreground font-medium truncate max-w-[250px]">{post.title}</span>
            </nav>
          </div>
        </div>

        {/* Article Header */}
        <section className="py-10 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <Badge className="mb-4 bg-primary/10 text-primary border-0">{post.category}</Badge>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground leading-tight mb-6" data-testid="blog-post-title">
                {post.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-8">
                <span className="flex items-center gap-1.5">
                  <User className="w-4 h-4" />
                  {post.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  {new Date(post.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4" />
                  {post.readTime}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Image */}
        <div className="container mx-auto px-4 mb-10">
          <div className="max-w-4xl mx-auto rounded-2xl overflow-hidden">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-64 sm:h-80 md:h-96 object-cover"
            />
          </div>
        </div>

        {/* Article Content */}
        <section className="pb-16 bg-background" data-testid="blog-post-content">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <article className="space-y-6">
                {post.content.map((block, index) => {
                  if (block.type === 'heading') {
                    return (
                      <h2 key={index} className="text-2xl sm:text-3xl font-display font-bold text-foreground pt-6">
                        {block.text}
                      </h2>
                    );
                  }
                  if (block.type === 'subheading') {
                    return (
                      <h3 key={index} className="text-xl font-bold text-foreground pt-3">
                        {block.text}
                      </h3>
                    );
                  }
                  if (block.type === 'paragraph') {
                    return (
                      <p key={index} className="text-base text-muted-foreground leading-relaxed">
                        {block.text}
                      </p>
                    );
                  }
                  if (block.type === 'list') {
                    return (
                      <ul key={index} className="space-y-2.5 pl-1">
                        {block.items.map((item, i) => (
                          <li key={i} className="flex items-start gap-3 text-base text-muted-foreground leading-relaxed">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-2.5" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    );
                  }
                  return null;
                })}
              </article>

              {/* FAQ Section */}
              {post.faq && post.faq.length > 0 && (
                <div className="mt-16 pt-10 border-t border-border" data-testid="blog-faq-section">
                  <h2 className="text-2xl font-display font-bold text-foreground mb-8">
                    Frequently Asked Questions
                  </h2>
                  <div className="space-y-5">
                    {post.faq.map((item, index) => (
                      <div key={index} className="p-5 rounded-xl bg-muted/30 border border-border">
                        <h3 className="text-base font-bold text-foreground mb-2">{item.question}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">{item.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA */}
              <div className="mt-14 p-8 rounded-2xl bg-gradient-to-r from-primary/10 via-gold/10 to-primary/10 border border-primary/20 text-center">
                <h3 className="text-xl font-bold text-foreground mb-3">
                  Ready to Start Your Supplement Journey?
                </h3>
                <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
                  Shop 100% authentic supplements at NutraFuel. Trusted by athletes across India and Nepal.
                </p>
                <div className="flex flex-wrap gap-3 justify-center">
                  <Button asChild>
                    <Link to="/products" data-testid="blog-cta-products">Shop Products</Link>
                  </Button>
                  <Button variant="outline" asChild>
                    <a
                      href="https://wa.me/9779824042067?text=Hi!%20I%20read%20your%20blog%20and%20need%20supplement%20advice."
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="blog-cta-whatsapp"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Ask an Expert
                    </a>
                  </Button>
                </div>
              </div>

              {/* Tags */}
              <div className="mt-10 flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="text-xs">
                    #{tag}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="py-14 bg-muted/20 border-t border-border" data-testid="related-posts-section">
            <div className="container mx-auto px-4">
              <h2 className="text-2xl font-display font-bold text-foreground mb-8">More Articles</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedPosts.map((rp) => (
                  <Link key={rp.id} to={`/blog/${rp.slug}`} data-testid={`related-blog-${rp.id}`}>
                    <Card className="group overflow-hidden border-0 shadow-soft hover:shadow-elevated transition-all duration-300 hover:-translate-y-1 h-full">
                      <div className="aspect-video overflow-hidden bg-muted">
                        <img
                          src={rp.image}
                          alt={rp.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <CardContent className="p-5">
                        <Badge variant="secondary" className="text-xs mb-2">{rp.category}</Badge>
                        <h3 className="font-bold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                          {rp.title}
                        </h3>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </Layout>
    </>
  );
};

export default BlogPostPage;
